import { useState, useEffect } from 'react';
import {
  Upload, Plus, Trash2, Edit3, X, FolderOpen, Clock
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';

// ─── Types ──────────────────────────────────────────

type ImportSourceType = 'CSV' | 'EXCEL' | 'JSON' | 'XML' | 'FILE_SYSTEM' | 'API' | 'LOCAL_FILE' | 'REMOTE_WORKER';

interface ImportConfig {
  id: string;
  name: string;
  organizationId: string;
  sourceType: ImportSourceType;
  filePath?: string;
  filePattern?: string;
  schedule?: string;
  isActive: boolean;
  fieldMappings: ImportFieldMapping[];
}

interface ImportFieldMapping {
  id: string;
  dataFieldId: string;
  sourceColumn: string;
  transformFn?: string;
  sortOrder?: number;
  dataField?: { id: string; name: string; type: string };
}

interface DataField {
  id: string;
  name: string;
  type: string;
}

const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-colors';
const labelClass = 'text-sm text-gray-700 block mb-1';

const SOURCE_LABELS: Record<ImportSourceType, string> = {
  CSV: 'CSV-Datei', EXCEL: 'Excel-Datei', JSON: 'JSON-Datei', XML: 'XML-Datei', FILE_SYSTEM: 'Dateiordner (Watcher)', LOCAL_FILE: 'Lokale Datei (Worker)', REMOTE_WORKER: 'Remote Worker',
};

export default function ImportConfigPage() {
  const { user } = useAuth();
  const orgId = user?.organizationId || '';

  const [configs, setConfigs] = useState<ImportConfig[]>([]);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [fields, setFields] = useState<DataField[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, any>>({
    name: '', organizationId: '', sourceType: 'CSV', filePath: '', filePattern: '', schedule: '', isActive: true,
  });

  // Field Mapping Editor
  const [showMappings, setShowMappings] = useState<string | null>(null);
  const [newMapping, setNewMapping] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!orgId) return;
    loadData();
  }, [orgId]);

  async function loadData() {
    setLoading(true);
    api.get('/organizations', { params: { limit: 100 } }).then(res => setOrgs(Array.isArray(res.data) ? res.data : (res.data?.data || []))).catch(() => {});
    try {
      const [cfgRes, fieldRes] = await Promise.all([
        api.get(`/import-configs?organizationId=${orgId}`),
        api.get(`/data-sources/fields?organizationId=${orgId}`),
      ]);
      setConfigs(cfgRes.data || []);
      setFields(fieldRes.data || []);
    } catch { /* silent */ }
    setLoading(false);
  }

  function openCreate() {
    setForm({ name: '', organizationId: orgId || '', sourceType: 'CSV', filePath: '', filePattern: '', schedule: '', isActive: true });
    setEditingId(null);
    setShowForm(true);
  }

  function openEdit(c: ImportConfig) {
    setForm({
      name: c.name, sourceType: c.sourceType,
      filePath: c.filePath || '', filePattern: c.filePattern || '',
      schedule: c.schedule || '', isActive: c.isActive,
    });
    setEditingId(c.id);
    setShowForm(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/import-configs/${editingId}`, form);
      } else {
        await api.post('/import-configs', { ...form, organizationId: form.organizationId || orgId });
      }
      setShowForm(false);
      loadData();
    } catch (err: any) {
      alert(`Fehler: ${err.response?.data?.message || err.message}`);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Import-Konfiguration wirklich löschen?')) return;
    await api.delete(`/import-configs/${id}`);
    loadData();
  }

  async function addFieldMapping(configId: string) {
    if (!newMapping.dataFieldId || !newMapping.sourceColumn) return;
    try {
      await api.post(`/import-configs/${configId}/field-mappings`, newMapping);
      setNewMapping({});
      loadData();
    } catch (err: any) {
      alert(`Fehler: ${err.response?.data?.message || err.message}`);
    }
  }

  async function removeFieldMapping(mappingId: string) {
    try {
      await api.delete(`/import-configs/field-mappings/${mappingId}`);
      loadData();
    } catch (err: any) {
      alert(`Fehler: ${err.response?.data?.message || err.message}`);
    }
  }

  async function triggerImport(configId: string) {
    try {
      // Manuelle Auslösung: ImportConfig aktivieren (der Watcher verarbeitet es automatisch)
      const config = configs.find(c => c.id === configId);
      if (config) {
        await api.put(`/import-configs/${configId}`, { isActive: !config.isActive });
        await api.put(`/import-configs/${configId}`, { isActive: true });
        alert('Import-Konfiguration aktiviert. Der Watcher verarbeitet die nächste Datei automatisch.');
        loadData();
      }
    } catch (err: any) {
      alert(`Fehler: ${err.response?.data?.message || err.message}`);
    }
  }

  if (loading) return <div className="p-8 text-center text-gray-400">Lade...</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Upload size={28} className="text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Import-Konfigurationen</h1>
            <p className="text-sm text-gray-500">Automatische Imports & Dateiordner-Überwachung</p>
          </div>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm">
          <Plus size={16} /> Import-Config
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">{editingId ? 'Import-Konfiguration bearbeiten' : 'Neue Import-Konfiguration'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className={labelClass}>Name *</label>
                <input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z.B. LV Wien CSV-Import" required />
              </div>
              <div>
                <label className={labelClass}>Organisation</label>
                <select className={inputClass} value={form.organizationId || orgId} onChange={(e) => setForm({ ...form, organizationId: e.target.value })}>
                  {orgs.map((o: any) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Quell-Typ</label>
                <select className={inputClass} value={form.sourceType} onChange={(e) => setForm({ ...form, sourceType: e.target.value })}>
                  {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              {form.sourceType === 'FILE_SYSTEM' && (
                <>
                  <div>
                    <label className={labelClass}>Datei-Pfad (Watcher)</label>
                    <input className={inputClass} value={form.filePath} onChange={(e) => setForm({ ...form, filePath: e.target.value })} placeholder="/var/imports/lv-wien" />
                    <p className="text-xs text-gray-400 mt-1">Verzeichnis das auf neue Dateien überwacht wird</p>
                  </div>
                  <div>
                    <label className={labelClass}>Datei-Pattern (Glob)</label>
                    <input className={inputClass} value={form.filePattern} onChange={(e) => setForm({ ...form, filePattern: e.target.value })} placeholder="*.csv" />
                  </div>
                  <div>
                    <label className={labelClass}>Cron-Plan (optional)</label>
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-gray-400" />
                      <input className={inputClass} value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} placeholder="0 */6 * * *" />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Leer lassen = nur manuell. Cron-Format: Minute Stunde Tag Monat Wochentag</p>
                  </div>
                </>
              )}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                  <span className="text-sm text-gray-700">Aktiv</span>
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-sm">Abbrechen</button>
                <button type="submit" className="btn-primary text-sm">{editingId ? 'Speichern' : 'Erstellen'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Config List */}
      {configs.length === 0 ? (
        <div className="card text-center py-12">
          <FolderOpen size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">Keine Import-Konfigurationen definiert.</p>
          <p className="text-xs text-gray-400 mt-1">Konfiguriere CSV/Excel-Imports oder Dateiordner-Überwachung.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {configs.map((cfg) => (
            <div key={cfg.id} className="card">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${cfg.sourceType === 'FILE_SYSTEM' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}`}>
                    <FolderOpen size={16} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{cfg.name}</h3>
                      <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{SOURCE_LABELS[cfg.sourceType]}</span>
                      {cfg.isActive ? <span className="text-xs text-green-600">● Aktiv</span> : <span className="text-xs text-gray-400">○ Inaktiv</span>}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5 space-x-3">
                      {cfg.filePath && <span>📁 {cfg.filePath}</span>}
                      {cfg.filePattern && <span>📄 {cfg.filePattern}</span>}
                      {cfg.schedule && <span>⏰ {cfg.schedule}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <button onClick={() => triggerImport(cfg.id)} className="p-1.5 text-gray-400 hover:text-green-600" title="Jetzt importieren"><Upload size={14} /></button>
                  <button onClick={() => openEdit(cfg)} className="p-1.5 text-gray-400 hover:text-blue-600"><Edit3 size={15} /></button>
                  <button onClick={() => handleDelete(cfg.id)} className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              </div>

              {/* Field Mappings */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <button onClick={() => setShowMappings(showMappings === cfg.id ? null : cfg.id)}
                  className="text-xs text-primary font-medium hover:underline flex items-center gap-1">
                  {showMappings === cfg.id ? '▼' : '▶'} Feld-Mappings ({cfg.fieldMappings?.length || 0})
                </button>

                {showMappings === cfg.id && (
                  <div className="mt-2 space-y-2">
                    {cfg.fieldMappings?.map((m) => (
                      <div key={m.id} className="flex items-center justify-between bg-gray-50 rounded px-3 py-1.5 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 mr-1">#{m.sortOrder ?? "?"}</span>
                          <span className="font-mono text-xs text-gray-500">{m.sourceColumn}</span>
                          <span className="text-gray-300">→</span>
                          <span className="font-medium text-gray-700">{m.dataField?.name || '?'}</span>
                          <span className="text-xs text-gray-400">({m.dataField?.type})</span>
                        </div>
                        <button onClick={() => removeFieldMapping(m.id)} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
                      </div>
                    ))}

                    <div className="flex items-center gap-2 bg-gray-50 rounded px-3 py-2">
                      <select className="text-xs border rounded px-2 py-1 flex-1" value={newMapping.dataFieldId || ''}
                        onChange={(e) => setNewMapping({ ...newMapping, dataFieldId: e.target.value })}>
                        <option value="">— Datenfeld wählen —</option>
                        {fields.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.type})</option>)}
                      </select>
                      <input className="text-xs border rounded px-2 py-1 w-20" placeholder="Spaltenname" value={newMapping.sourceColumn || ''}
                        onChange={(e) => setNewMapping({ ...newMapping, sourceColumn: e.target.value })} />
                      <input type="number" className="text-xs border rounded px-2 py-1 w-12" placeholder="#"
                        value={newMapping.sortOrder ?? ""}
                        onChange={(e) => setNewMapping({ ...newMapping, sortOrder: parseInt(e.target.value) || 0 })} min="0" title="Reihenfolge" />
                      <button onClick={() => addFieldMapping(cfg.id)} className="btn-primary text-xs px-2 py-1">+</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
